import { Paper, Tooltip } from '@mui/material';
import { GridRenderCellParams, GridSortModel } from '@mui/x-data-grid';
import { useCallback, useState } from 'react';
import { Link as NavLink } from 'react-router-dom';
import {
  useRecoilStateLoadable,
  useRecoilValueLoadable,
  useSetRecoilState,
} from 'recoil';
import { trackEditNumber } from 'src/common/userTracking';
import { v4 as uuidv4 } from 'uuid';
import { Role, user } from '../../recoil/atoms/user';
import { inboundSummary } from '../../recoil/selectors/inboundSummary';
import {
  methodNameEnum,
  refetchToggle,
} from '../../recoil/selectors/makePHPRequest';
import { permissionsSelector } from '../../recoil/selectors/permissionsSelector';
import {
  endpointEnum,
  makeAPIRequest,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { EditNumberModal } from '../components/modals/EditNumberModal';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import dataTableScss from '../modules/Manage/pages/queues-table/data-table.module.css';
import {
  DataTable,
  IColDef,
} from '../modules/Manage/pages/queues-table/DataTable';
import { EnhancedTableToolbar } from '../modules/Manage/pages/queues-table/EnhancedTableToolbar';
import { QueuesSkeleton } from '../modules/Skeleton/QueuesSkeleton';

export function InboundSummary() {
  const [userAtom] = useRecoilStateLoadable(user);
  const { role } = useRecoilValueLoadable(permissionsSelector).contents;
  const data = {
    call: methodNameEnum.GET_BUSINESS_NUMBERS,
  };
  const businessNumbers = useRecoilValueLoadable(
    makeAPIRequest({
      endpoint: endpointEnum.GET_BUSINESS_NUMBERS,
      payloadJsonString: JSON.stringify(data),
    })
  );
  const setRefetchToggleForNumbers = useSetRecoilState(
    refetchToggle(endpointEnum.GET_BUSINESS_NUMBERS)
  );
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [editModal, handleEditModal] = useState(false);
  const [rowForEdit, handleRowForEdit] = useState({});
  function handleEdit(row) {
    const editNumber = businessNumbers.contents?.response?.data?.result?.find(
      (el) => el.Numberid === row.numberid
    );
    handleEditModal(true);
    handleRowForEdit(editNumber);
  }
  const [
    businessSMSRulesLoadable,
    setRefetchToggleForSMSRules,
  ] = makeApiRequestCached(
    {
      endpoint: endpointEnum.GET_BUSINESS_SMS_BY_BUSINESS,
      payloadJsonString: JSON.stringify({
        call: methodNameEnum.GET_BUSINESS_SMS_BY_BUSINESS,
        apikey: userAtom?.contents?.apikey,
        businessid: userAtom?.contents?.businessid,
        businessId: userAtom?.contents?.businessid,
      }),
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.result?.reduce(
        (acc, item) => ({
          ...acc,
          nameLookup: {
            ...acc.nameLookup,
            [item.name]: item,
          },
        }),
        {
          nameLookup: {},
        }
      ) ?? cached.current
  );
  const isUser = role === Role.USER;
  function phoneNumberFormatter(params: GridRenderCellParams) {
    return isUser ? (
      params.value
    ) : (
      <Tooltip title="Edit Number">
        <NavLink
          disabled={businessNumbers.state === 'loading'}
          onClick={() => {
            trackEditNumber();
            handleEdit(params.row);
          }}
          to="#"
        >
          {params.value}
        </NavLink>
      </Tooltip>
    );
  }
  const columns = [
    {
      field: 'number',
      headerName: 'Number',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: phoneNumberFormatter,
    },
    {
      field: 'name',
      headerName: 'Name',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'destinationName',
      headerName: 'Destination',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      renderCell: (params) => {
        // eslint-disable-next-line
        //console.log(params, 'params','role', role);
        if (role === 'superadmin' || role === 'admin' || role === 'manager') {
          switch (params.row.type) {
            case 'IVR':
              return isUser ? (
                params.value
              ) : (
                <NavLink to={`/manage/ivrs/${params.row.destinationid}/edit`}>
                  <p>{params.value}</p>
                </NavLink>
              );
            case 'Group':
              return isUser ? (
                params.value
              ) : (
                <NavLink
                  to={`/manage/ring-groups/${params.row.destinationid}/edit`}
                >
                  <p>{params.value}</p>
                </NavLink>
              );
            case 'Agent':
              return isUser ? (
                params.value
              ) : (
                <NavLink
                  to={`/manage/agents/${params.row.destinationid}/agent-reporting`}
                >
                  <p>{params.value}</p>
                </NavLink>
              );
            case 'Queue':
              return isUser ? (
                params.value
              ) : (
                <NavLink to={`/manage/queues/${params.row.destinationid}/edit`}>
                  <p>{params.value}</p>
                </NavLink>
              );
            case 'Conference':
              return isUser ? (
                params.value
              ) : (
                <NavLink
                  to={`/manage/conferences/${params.row.destinationid}/edit`}
                >
                  <p>{params.value}</p>
                </NavLink>
              );
            case 'unallocated':
              return <div>{params.value}</div>;
            case 'Local Presence':
              return <div>{params.value}</div>;
          }
        } else {
          return <div>{params.value}</div>;
        }
      },
    },
    {
      field: 'allincoming',
      headerName: 'Incoming',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'incomingconnection',
      headerName: 'Incoming Connections',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'allmissed',
      headerName: 'Missed',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
    },
    {
      field: 'incomingmissed_pct',
      headerName: 'Missed %',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      sortComparator: (a, b) => {
        return (
          Number(typeof a === 'string' && a.replaceAll('%', '')) -
          Number(typeof b === 'string' && b.replaceAll('%', ''))
        );
      },
    },
    {
      field: 'allavgduration',
      headerName: 'Avg. Duration',
      headerClassName: dataTableScss.fullWidth,
      cellClassName: dataTableScss.fullWidth,
      sortComparator: (a, b) => Number(a) - Number(b),
    },
  ] /*satisfies*/ as IColDef[];
  const sortModel: GridSortModel = [
    {
      field: 'allincoming',
      sort: 'desc',
    },
  ];
  const ToolbarComponent = useCallback(
    (props) => <EnhancedTableToolbar {...props} />,
    []
  );
  const inbound = useRecoilValueLoadable(inboundSummary);
  let editable = [];
  if (inbound.state === 'hasValue') {
    editable = inbound?.contents?.map((o) => ({
      ...o,
      id: uuidv4(),
    }));
  }

  return (
    <Paper elevation={0}>
      <div>
        {inbound.state === 'loading' && <QueuesSkeleton />}
        <DataTable
          ToolbarComponent={ToolbarComponent}
          rows={editable}
          columns={columns}
          sortModel={sortModel}
          loading={inbound.state === 'loading'}
        />

        <EditNumberModal
          show={!!editModal}
          handleClose={handleEditModal}
          methodNameEnum={methodNameEnum}
          enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
          number={rowForEdit}
          setRefetchToggleForNumbers={setRefetchToggleForNumbers}
          businessSMSRulesLoadable={businessSMSRulesLoadable}
          setRefetchToggleForSMSRules={setRefetchToggleForSMSRules}
        />
      </div>
    </Paper>
  );
}
